/**
 * Phase 20.1 — Shared client-side quote math.
 *
 * Mirror of the canonical server-side recompute in `/app/backend/admin_phase2.py`
 * (`_recompute_quote_totals`). Used by NewQuoteDialog (creation flow) and
 * QuoteBasisEditor (post-creation edit flow) to give the admin a LIVE
 * subtotal/VAT/total preview that matches what the backend will persist.
 *
 * Single source of truth — if the math changes server-side, update here too.
 */

export function num(v) {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
}

/**
 * BTW-percentage fallback that respects 0 as a deliberate admin choice.
 * Plain `n(v) || 21` is the classic bug — 0 is falsy and silently becomes 21.
 * Returns 21 ONLY when the input is literally empty/null/undefined or invalid.
 */
export function vatOrDefault(v) {
  if (v === "" || v === null || v === undefined) return 21;
  const x = Number(v);
  return Number.isFinite(x) && x >= 0 ? x : 21;
}

export function round2(v) {
  return Math.round((num(v) + 1e-9) * 100) / 100;
}

export function round4(v) {
  return Math.round((num(v) + 1e-9) * 10000) / 10000;
}

/** Auto-CBM uit L × B × H (cm) → m³. Honourt expliciete cbm-override. */
export function autoCbm(line) {
  if (line.cbm !== "" && line.cbm != null) return round4(line.cbm);
  const L = num(line.length_cm), W = num(line.width_cm), H = num(line.height_cm);
  if (L > 0 && W > 0 && H > 0) return round4((L * W * H) / 1_000_000);
  return 0;
}

export function lineTotal(line) {
  const qty = num(line.quantity) || 1;
  const rate = Math.max(0, num(line.rate));
  const lt = (line.line_type || "cbm").toLowerCase();
  if (lt === "fixed") return round2(rate);
  if (lt === "qty") return round2(qty * rate);
  return round2(qty * autoCbm(line) * rate);
}

/**
 * Compute subtotal/VAT/total for one of the 3 basis modes.
 * @param {("lines"|"total_volume"|"fixed")} mode
 * @param {object} input — { line_items, total_volume_cbm, fixed_amount, pricing }
 */
export function computeBasisTotals(mode, input) {
  const pricing = input.pricing || {};
  const extras = ["admin_fee", "documentation_fee", "pickup_fee", "callout_fee", "km_fee", "extra_handling"]
    .reduce((s, k) => s + Math.max(0, num(pricing[k])), 0);
  const discount = Math.max(0, num(pricing.discount));
  let base = 0;
  if (mode === "lines") {
    base = (input.line_items || []).reduce((s, l) => s + lineTotal(l), 0);
  } else if (mode === "total_volume") {
    base = Math.max(0, num(input.total_volume_cbm)) * Math.max(0, num(pricing.price_per_cbm));
  } else if (mode === "fixed") {
    base = Math.max(0, num(input.fixed_amount));
  }
  const sub = Math.max(0, round2(base + extras - discount));
  const vatPct = vatOrDefault(pricing.vat_percent);
  const vat = round2(sub * (vatPct / 100));
  const total = round2(sub + vat);
  return { sub, vat, total, base, extras, discount, vatPct };
}
